import React, { useMemo } from "react";
import {
    Page,
    Text,
    View,
    Document,
    StyleSheet,
    Image,
    Font,
} from "@react-pdf/renderer";
import { GuestBook } from "@models/GuestBook";
import { formatDate } from "@util/string";
import { withConditionalCacheBust } from "@util/cache-bust";

Font.register({
    family: "YesevaOne",
    src: "/assets/fonts/YesevaOne-Regular.ttf",
});

Font.register({
    family: "QEDavidReid",
    src: "/assets/fonts/QEDavidReid.ttf",
});

Font.registerEmojiSource({
    format: "png",
    url: "https://cdnjs.cloudflare.com/ajax/libs/twemoji/14.0.2/72x72/",
});

export type Orientation = "portrait" | "landscape";
export type PageSize = "A4" | "A5";

export interface GuestBookDocumentProps {
    guestBook: GuestBook;
    orientation: Orientation;
    pageSize: PageSize;
}

const FIOH_LOGO = "/assets/images/fioh-logo.png";

const getFontSizeForNameAndDate = (name: string)
: { nameFontSize: number; dateFontSize: number } => {
    if (name.length > 40) return { nameFontSize: 18, dateFontSize: 12 };
    if (name.length > 30) return { nameFontSize: 22, dateFontSize: 14 };
    return { nameFontSize: 28, dateFontSize: 16 };
};

const GuestBookDocument: React.FC<GuestBookDocumentProps> = ({
    guestBook,
    orientation,
    pageSize,
}) => {
    const isA5Landscape = orientation === "landscape" && pageSize === "A5";

    const BASE = {
        imageHeight: pageSize === "A4" ? 270 : 220,
        entryName: 18,
        entryMessage: 16,
        pageNumber: 10,
        businessLogo: 40,
        footerLogo: 35,
    };

    const scale = useMemo(() => (pageSize === "A5" ? 0.8 : 1), [pageSize]);
    const clamp = (v: number, min: number) => Math.max(min, Math.round(v));

    const { nameFontSize, dateFontSize } = useMemo(
        () => getFontSizeForNameAndDate(`${guestBook.firstName} ${guestBook.lastName}`),
        [guestBook.firstName, guestBook.lastName],
    );

    const scaledNameFontSize = clamp(nameFontSize * scale, 14);
    const scaledDateFontSize = clamp(dateFontSize * scale, 10);

    const computed = useMemo(
        () => ({
            image: { ...styles.image, height: Math.round(BASE.imageHeight * scale) },
            entryName: { ...styles.entryName, fontSize: clamp(BASE.entryName * scale, 12) },
            entryMessage: { ...styles.entryMessage, fontSize: clamp(BASE.entryMessage * scale, 10) },
            pageNumber: { ...styles.pageNumber, fontSize: clamp(BASE.pageNumber * scale, 8) },
            businessLogo: {
                ...styles.businessLogo,
                height: Math.round(BASE.businessLogo * scale),
                marginTop: isA5Landscape ? -10 : 0, // move logo up
            },
            footerLogo: {
                ...styles.logo,
                height: Math.round(BASE.footerLogo * scale),
            },
            title: {
                ...styles.title,
                marginTop: isA5Landscape ? -6 : 10, // move name up
            },
            dates: {
                ...styles.dates,
                marginTop: isA5Landscape ? 0 : 5, // move dates up a bit
            },
            bodyFooter: {
                ...styles.bodyFooter,
                bottom: isA5Landscape ? 4 : 12, // push footer further down
            },
            titleFooter: {
                ...styles.titleFooter,
                bottom: orientation === "landscape" ? 14 : 24, // adjust per orientation
            },
        }),
        [scale, isA5Landscape, orientation],
    );

    return (
        <Document>
            {/* Cover Page */}
            <Page size={pageSize} orientation={orientation} style={styles.page}>
                <View style={styles.pageContentContainer}>
                    <View style={styles.header} fixed>
                        {guestBook.businessLogo && (
                            <Image
                                src={{ uri: withConditionalCacheBust(guestBook.businessLogo) }}
                                style={computed.businessLogo}
                            />
                        )}
                    </View>

                    <View style={styles.coverContent}>
                        {guestBook.image && (
                            <Image
                                src={{ uri: withConditionalCacheBust(guestBook.image) }}
                                style={computed.image}
                            />
                        )}
                        <Text style={[computed.title, { fontSize: scaledNameFontSize }]}>
                            {`${guestBook.firstName} ${guestBook.lastName}`}
                        </Text>
                        {guestBook.birthDate && guestBook.deathDate && (
                            <Text style={[computed.dates, { fontSize: scaledDateFontSize }]}>
                                {`${formatDate(guestBook.birthDate)} — ${formatDate(guestBook.deathDate)}`}
                            </Text>
                        )}
                    </View>

                    <View style={computed.titleFooter} fixed>
                        <View style={styles.logoContainer}>
                            <Image src={FIOH_LOGO} style={computed.footerLogo} />
                        </View>
                    </View>
                </View>
            </Page>

            {/* Entries Page(s) */}
            <Page size={pageSize} orientation={orientation} style={styles.page}>
                <View style={styles.entriesSection}>
                    {guestBook.entries.map((entry) => (
                        <View key={entry.id} style={styles.entry} wrap={false}>
                            <Text style={computed.entryName}>{entry.name}</Text>
                            <Text style={computed.entryMessage}>{entry.message}</Text>
                        </View>
                    ))}
                </View>
                <View
                    style={[
                        styles.pageOutline,
                        { bottom: pageSize === "A5" ? 46 : 52 },
                    ]}
                    fixed
                />
                <View style={computed.bodyFooter} fixed>
                    <View style={styles.logoContainer}>
                        <Image src={FIOH_LOGO} style={computed.footerLogo} />
                    </View>
                    <Text style={computed.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber - 1} / ${totalPages - 1}`} />
                </View>
            </Page>
        </Document>
    );
};

const styles = StyleSheet.create({
    page: {
        padding: 40,
        fontSize: 12,
        color: "#111",
        fontFamily: "YesevaOne",
        backgroundColor: "#fff",
    },
    pageContentContainer: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between", // pushes footer down
        flexGrow: 1,
        padding: 26,
        border: "1px solid #c8c8c8",
        borderRadius: 8,
    },
    pageOutline: {
        position: "absolute",
        top: 40,
        left: 40,
        right: 40,
        border: "1px solid #c8c8c8",
        borderRadius: 8,
    },
    header: {
        alignItems: "center",
        marginBottom: 20,
    },
    businessLogo: {
        height: 40,
        width: "auto",
        objectFit: "contain",
    },
    coverContent: {
        flexGrow: 1,
        justifyContent: "center",
        textAlign: "center",
    },
    image: {
        height: 220,
        borderRadius: 16,
        marginBottom: 20,
        marginLeft: "auto",
        marginRight: "auto",
        marginTop: -32,
    },
    title: {
        fontWeight: "bold",
        marginTop: 10,
        marginBottom: 5,
    },
    dates: {
        color: "#555",
        marginVertical: 5,
    },
    entriesSection: {

    },
    entry: {
        marginTop: 12,
        marginHorizontal: 20,
        marginBottom: 4,
        paddingBottom: 12,
        borderBottom: "1px solid #e0e0e0",
    },
    entryName: {
        marginBottom: 4,
        fontFamily: "QEDavidReid",
        color: "#222",
    },
    entryMessage: {
        fontFamily: "QEDavidReid",
        color: "#444",
        lineHeight: 1.4,
    },
    titleFooter: {
        position: "absolute",
        bottom: 30,
        left: 0,
        right: 0,
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        paddingHorizontal: 40,
        color: "#777",
    },
    bodyFooter: {
        position: "absolute",
        bottom: 12,
        left: 0,
        right: 0,
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        paddingHorizontal: 40,
        color: "#777",
    },
    logoContainer: {
        flex: 1,
        alignItems: "center",
    },
    logo: {
        height: 35,
    },
    pageNumber: {
        fontSize: 10,
        color: "#777",
        textAlign: "right",
    },
});

export default GuestBookDocument;
